import { useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtNum } from "@/utils/toEnglishDigits";
import {
  ShieldCheck,
  Warning,
  Trash,
  X as XIcon,
  CheckCircle,
  CurrencyDollar,
} from "@phosphor-icons/react";

const CONFIRM_PHRASE = "RESET-MARASIM";

// -------- Official Pricing Seed panel --------------------------------------
// One-shot data-fix UI for the GM. Calls two admin endpoints:
//   GET  /api/admin/seed-official-pricing        → dry-run (returns diff)
//   POST /api/admin/seed-official-pricing        → commit
// Both endpoints use `route_rules` and are idempotent, so the button is safe
// to click multiple times. Purge is ON by default (stale rules are deleted).
function PricingSeedPanel() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run report
  const [applied, setApplied] = useState(null); // committed report

  const loadPreview = async () => {
    setBusy(true);
    setApplied(null);
    try {
      const { data } = await api.get("/admin/seed-official-pricing");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الفحص");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/seed-official-pricing");
      setApplied(data);
      setPreview(null);
      toast.success("تم تطبيق التسعير الرسمي بنجاح");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التطبيق");
    } finally {
      setBusy(false);
    }
  };

  const t = preview?.totals || applied?.totals;
  const show = preview || applied;

  return (
    <section
      data-testid="pricing-seed-panel"
      className="rounded-2xl border border-primary/25 bg-gradient-to-br from-[#0F2148] to-[#172956] p-5 md:p-6"
    >
      <div className="flex items-center gap-3 pb-3 mb-4 border-b border-primary/20">
        <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center">
          <CurrencyDollar size={22} weight="duotone" className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white">التسعيرة الرسمية</h2>
          <p className="text-white/60 text-sm mt-0.5">
            مزامنة قواعد الأسعار (route_rules) مع المواصفات المعتمدة (7 سيارات × 15 مسار = 105 قاعدة).
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button
          type="button"
          onClick={loadPreview}
          disabled={busy}
          data-testid="pricing-seed-preview-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary/15 border border-primary/50 text-primary text-sm font-bold hover:bg-primary/25 disabled:opacity-50"
        >
          🔍 فحص أولي (بدون تغيير)
        </button>
        {preview && (preview.totals.new + preview.totals.updated + preview.totals.purged > 0) && (
          <button
            type="button"
            onClick={commit}
            disabled={busy}
            data-testid="pricing-seed-apply-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#22C55E] text-white text-sm font-bold hover:bg-[#16A34A] disabled:opacity-50"
          >
            ✅ تطبيق التغييرات ({fmtNum(preview.totals.new + preview.totals.updated + preview.totals.purged)})
          </button>
        )}
        {preview && preview.totals.new + preview.totals.updated + preview.totals.purged === 0 && (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#22C55E]/15 border border-[#22C55E]/40 text-[#4ADE80]">
            <CheckCircle size={13} weight="fill" /> قاعدة البيانات مطابقة تماماً — لا توجد تغييرات
          </span>
        )}
      </div>

      {show && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <div className="rounded-xl bg-black/30 border border-white/10 p-3 text-center">
            <div className="text-[11px] text-white/60 mb-0.5">جديدة</div>
            <div className="text-primary text-xl font-extrabold num">{fmtNum(t.new)}</div>
          </div>
          <div className="rounded-xl bg-black/30 border border-white/10 p-3 text-center">
            <div className="text-[11px] text-white/60 mb-0.5">محدَّثة</div>
            <div className="text-[#F59E0B] text-xl font-extrabold num">{fmtNum(t.updated)}</div>
          </div>
          <div className="rounded-xl bg-black/30 border border-white/10 p-3 text-center">
            <div className="text-[11px] text-white/60 mb-0.5">مطابقة</div>
            <div className="text-[#4ADE80] text-xl font-extrabold num">{fmtNum(t.unchanged)}</div>
          </div>
          <div className="rounded-xl bg-black/30 border border-white/10 p-3 text-center">
            <div className="text-[11px] text-white/60 mb-0.5">محذوفة</div>
            <div className="text-[#EF4444] text-xl font-extrabold num">{fmtNum(t.purged)}</div>
          </div>
        </div>
      )}

      {/* Detailed diff — dry-run only */}
      {preview && (preview.updated.length > 0 || preview.new.length > 0 || preview.purged.length > 0) && (
        <details className="rounded-xl bg-black/30 border border-white/10 p-3 text-xs text-white/85">
          <summary className="cursor-pointer text-white/70 hover:text-white select-none">
            عرض تفاصيل التغييرات ({fmtNum(preview.updated.length + preview.new.length + preview.purged.length)})
          </summary>
          <div className="mt-3 space-y-2 max-h-72 overflow-y-auto num" dir="rtl">
            {preview.updated.map((r, i) => (
              <div key={`u${i}`} className="flex justify-between items-center gap-2 py-1 border-b border-white/5">
                <span className="text-[#F59E0B] font-bold text-[11px]">تحديث</span>
                <span className="flex-1 truncate">
                  <span className="text-white/50">{r.car_type}</span>{" "}
                  {r.pickup} ← {r.destination}
                </span>
                <span dir="ltr" className="text-white/70">
                  {fmtNum(r.old_price)} → <span className="text-primary font-bold">{fmtNum(r.new_price)}</span>
                </span>
              </div>
            ))}
            {preview.new.map((r, i) => (
              <div key={`n${i}`} className="flex justify-between items-center gap-2 py-1 border-b border-white/5">
                <span className="text-primary font-bold text-[11px]">جديد</span>
                <span className="flex-1 truncate">
                  <span className="text-white/50">{r.car_type}</span>{" "}
                  {r.pickup} ← {r.destination}
                </span>
                <span dir="ltr" className="text-primary font-bold">{fmtNum(r.price)}</span>
              </div>
            ))}
            {preview.purged.map((r, i) => (
              <div key={`p${i}`} className="flex justify-between items-center gap-2 py-1 border-b border-white/5">
                <span className="text-[#EF4444] font-bold text-[11px]">حذف</span>
                <span className="flex-1 truncate">
                  <span className="text-white/50">{r.car_type}</span>{" "}
                  {r.pickup} ← {r.destination}
                </span>
                <span dir="ltr" className="text-white/50 line-through">{fmtNum(r.old_price)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {applied && (
        <div className="mt-2 rounded-xl bg-[#22C55E]/10 border border-[#22C55E]/40 p-3 text-[#86EFAC] text-sm">
          ✅ تم التطبيق بنجاح. اضغط <kbd className="px-1.5 py-0.5 rounded bg-black/40 text-[10px] mx-1">Ctrl+Shift+R</kbd>
          لتحديث الصفحة، ثم افتح أي حجز للتحقق من الأسعار الجديدة.
        </div>
      )}
    </section>
  );
}

// -------- Bulk Recalculate Prices panel ------------------------------------
// One-shot data-fix UI for GM. After the pricing seed or the smart-matcher
// bug fix, historical bookings still hold their OLD prices (frozen at
// creation time). This panel walks every booking, re-runs the same rule-
// match logic used at PATCH-time, and persists the new totals.
//   GET  /api/admin/recalculate-prices           → dry-run diff report
//   POST /api/admin/recalculate-prices           → commit
function RecalculatePricesPanel() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  const loadPreview = async () => {
    setBusy(true); setApplied(null);
    try {
      const { data } = await api.get("/admin/recalculate-prices");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الفحص");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/recalculate-prices");
      setApplied(data);
      setPreview(null);
      toast.success("تم إعادة حساب الأسعار لجميع الحجوزات");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التطبيق");
    } finally { setBusy(false); }
  };

  const show = preview || applied;
  const t = show?.totals;

  return (
    <section
      data-testid="recalc-prices-panel"
      className="rounded-2xl border border-primary/25 bg-gradient-to-br from-[#0F2148] to-[#172956] p-5 md:p-6"
    >
      <div className="flex items-center gap-3 pb-3 mb-4 border-b border-primary/20">
        <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center">
          <CurrencyDollar size={22} weight="duotone" className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white">إعادة حساب الأسعار</h2>
          <p className="text-white/60 text-sm mt-0.5">
            يُعيد حساب سعر كل حجز موجود من قواعد التسعير الحالية. مفيد بعد تصحيح
            قواعد التسعير أو مسار المطار/المحطة.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button
          type="button"
          onClick={loadPreview}
          disabled={busy}
          data-testid="recalc-prices-preview-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary/15 border border-primary/50 text-primary text-sm font-bold hover:bg-primary/25 disabled:opacity-50"
        >
          🔍 فحص الحجوزات (بدون تغيير)
        </button>
        {preview && preview.totals.changed > 0 && (
          <button
            type="button"
            onClick={commit}
            disabled={busy}
            data-testid="recalc-prices-apply-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#22C55E] text-white text-sm font-bold hover:bg-[#16A34A] disabled:opacity-50"
          >
            ♻️ إعادة حساب {fmtNum(preview.totals.changed)} حجز
          </button>
        )}
        {preview && preview.totals.changed === 0 && (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#22C55E]/15 border border-[#22C55E]/40 text-[#4ADE80]">
            <CheckCircle size={13} weight="fill" /> كل الحجوزات بالأسعار الصحيحة
          </span>
        )}
      </div>

      {show && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="rounded-xl bg-black/30 border border-white/10 p-3 text-center">
            <div className="text-[11px] text-white/60 mb-0.5">تم فحصها</div>
            <div className="text-white text-xl font-extrabold num">{fmtNum(t.scanned)}</div>
          </div>
          <div className="rounded-xl bg-black/30 border border-white/10 p-3 text-center">
            <div className="text-[11px] text-white/60 mb-0.5">ستتغيّر</div>
            <div className="text-[#F59E0B] text-xl font-extrabold num">{fmtNum(t.changed)}</div>
          </div>
          <div className="rounded-xl bg-black/30 border border-white/10 p-3 text-center">
            <div className="text-[11px] text-white/60 mb-0.5">صحيحة أصلاً</div>
            <div className="text-[#4ADE80] text-xl font-extrabold num">{fmtNum(t.unchanged)}</div>
          </div>
        </div>
      )}

      {preview && preview.changed.length > 0 && (
        <details className="rounded-xl bg-black/30 border border-white/10 p-3 text-xs text-white/85">
          <summary className="cursor-pointer text-white/70 hover:text-white select-none">
            عرض تفاصيل الحجوزات ({fmtNum(preview.changed.length)})
          </summary>
          <div className="mt-3 space-y-2 max-h-72 overflow-y-auto num" dir="rtl">
            {preview.changed.map((c) => (
              <div key={c.booking_id} className="rounded-lg bg-black/20 border border-white/5 p-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-bold">{c.booking_number || c.booking_id?.slice(0, 8)}</span>
                  <span dir="ltr">
                    <span className="text-white/50 line-through">{fmtNum(c.old_total)}</span>
                    <span className="text-white/50 mx-1">→</span>
                    <span className="text-primary font-bold">{fmtNum(c.new_total)}</span>
                  </span>
                </div>
                {(c.route_diffs || []).map((rd) => (
                  <div key={rd.leg} className="flex justify-between items-center gap-2 text-[11px] text-white/70 pl-2">
                    <span>مسار {fmtNum(rd.leg)}: {rd.pickup} ← {rd.destination}</span>
                    <span dir="ltr">
                      <span className="line-through text-white/40">{fmtNum(rd.old)}</span>
                      <span className="mx-1">→</span>
                      <span className="text-[#4ADE80] font-bold">{fmtNum(rd.new)}</span>
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </details>
      )}

      {applied && (
        <div className="mt-2 rounded-xl bg-[#22C55E]/10 border border-[#22C55E]/40 p-3 text-[#86EFAC] text-sm">
          ✅ تم إعادة حساب {fmtNum(applied.totals.changed)} حجز. اضغط <kbd className="px-1.5 py-0.5 rounded bg-black/40 text-[10px] mx-1">Ctrl+Shift+R</kbd>
          لتحديث الصفحة، ثم افتح جدول الحجوزات للتحقق.
        </div>
      )}
    </section>
  );
}


export default function AdminSettings() {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const canReset = phrase === CONFIRM_PHRASE && !busy;
  // Only the general manager (abugena01@gmail.com + admin_type=general_manager)
  // can see + trigger the reset section. Everyone else — including other admins —
  // must not see even a placeholder for this dangerous section.
  const isGeneralManager =
    (user?.email || "").toLowerCase() === "abugena01@gmail.com" &&
    user?.admin_type === "general_manager";

  const executeReset = async () => {
    if (!canReset) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset", { confirmation: CONFIRM_PHRASE });
      setResult(data);
      toast.success("تم تصفير البيانات التشغيلية بنجاح");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "فشل التصفير";
      toast.error(msg);
      setBusy(false);
    }
  };

  const closeAll = () => {
    setModalOpen(false);
    setPhrase("");
    setResult(null);
    setBusy(false);
    // Reload so the app picks up the fresh (empty) state everywhere.
    if (result) window.location.reload();
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="px-4 py-6" data-testid="admin-settings-page">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Title */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center">
              <ShieldCheck size={22} weight="duotone" className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">الإعدادات</h1>
              <p className="text-white/50 text-sm mt-0.5">
                إعدادات النظام والصيانة — {user?.name || "المدير"}
              </p>
            </div>
          </div>

          {/* Placeholder for future settings sections */}
          <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-[#0F2148] to-[#172956] p-5 md:p-6">
            <h2 className="text-lg font-bold text-white mb-2">حالة النظام</h2>
            <p className="text-white/60 text-sm">
              كل الخدمات تعمل بشكل طبيعي. للتحقق من حسابات الأدمن استخدم{" "}
              <code className="px-1.5 py-0.5 rounded bg-black/40 text-primary text-xs">
                /api/health/admins
              </code>
              .
            </p>
          </div>

          {/* 💰 Official Pricing Seed — GM only */}
          {isGeneralManager && <PricingSeedPanel />}

          {/* ♻️ Bulk Recalculate Prices — GM only */}
          {isGeneralManager && <RecalculatePricesPanel />}

          {/* 🚨 DANGER ZONE — visible ONLY to the general manager */}
          {isGeneralManager && (
          <section
            data-testid="danger-zone"
            className="rounded-2xl border-2 border-[#EF4444]/60 bg-[#EF4444]/5 p-5 md:p-6"
          >
            <div className="flex items-center gap-3 pb-3 mb-4 border-b border-[#EF4444]/30">
              <div className="w-11 h-11 rounded-xl bg-[#EF4444]/15 border border-[#EF4444]/50 flex items-center justify-center">
                <Warning size={22} weight="fill" className="text-[#EF4444]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#EF4444]">منطقة خطرة</h2>
                <p className="text-white/60 text-sm mt-0.5">إجراءات لا يمكن التراجع عنها</p>
              </div>
            </div>

            <div className="rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30 p-4 md:p-5">
              <h3 className="text-white font-bold text-base md:text-lg mb-2">
                🗑️ تصفير جميع البيانات التشغيلية
              </h3>
              <p className="text-white/80 text-sm mb-3">
                سيتم <strong className="text-[#FCA5A5]">حذفها نهائياً</strong>:
              </p>
              <ul className="text-sm text-[#FCA5A5]/90 space-y-1 mb-4 pr-4 list-disc">
                <li>جميع السيارات (settings.cars)</li>
                <li>جميع الشركات والعملاء (users غير الأدمن)</li>
                <li>جميع السائقين (drivers + external_drivers)</li>
                <li>جميع الحجوزات (bookings)</li>
                <li>جميع الإشعارات (notifications_log)</li>
                <li>كشوف الحسابات والمعاملات (settlements + driver_transactions)</li>
                <li>العدادات وتجاوزات التواريخ (counters + date_overrides)</li>
              </ul>

              <p className="text-sm text-[#86EFAC] mb-2 font-semibold">
                <CheckCircle size={14} weight="fill" className="inline -mt-0.5 ml-1" />
                يبقى محفوظاً:
              </p>
              <ul className="text-sm text-[#86EFAC]/90 space-y-1 mb-5 pr-4 list-disc">
                <li>حسابات الأدمن (is_admin=True أو is_protected=True)</li>
                <li>إعدادات النظام (settings — بدون cars)</li>
                <li>قواعد المسارات (route_rules)</li>
                <li>بيانات التواصل والمدير العام ومدير الحجوزات</li>
              </ul>

              <button
                type="button"
                onClick={() => {
                  setModalOpen(true);
                  setPhrase("");
                  setResult(null);
                }}
                data-testid="open-reset-modal-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#EF4444] text-white text-sm font-bold hover:bg-[#DC2626] hover:shadow-[0_10px_30px_-10px_rgba(239,68,68,0.7)] transition-all"
              >
                <Trash size={16} weight="fill" /> تصفير جميع البيانات التشغيلية
              </button>
              <p className="text-[#EF4444] text-xs mt-3 font-semibold">
                ⚠️ لا يمكن التراجع عن هذا الإجراء
              </p>
            </div>
          </section>
          )}
        </div>
      </main>

      {/* Confirmation Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="reset-modal"
        >
          <div className="w-full max-w-md rounded-2xl border-2 border-[#EF4444]/60 bg-[#0F2148] p-5 md:p-6 shadow-2xl">
            {result ? (
              <>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={26} weight="fill" className="text-[#4ADE80]" />
                    <h3 className="text-lg font-bold text-white">تم التصفير بنجاح</h3>
                  </div>
                  <button
                    onClick={closeAll}
                    data-testid="reset-close-btn"
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    <XIcon size={20} weight="bold" />
                  </button>
                </div>
                <div className="rounded-xl bg-black/30 border border-white/10 p-3 text-xs text-white/80 space-y-1 max-h-64 overflow-y-auto num">
                  {Object.entries(result?.stats?.deleted || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-white/60">{k}</span>
                      <span className="text-primary font-bold">{fmtNum(v)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-1 border-t border-white/10">
                    <span className="text-white/60">cars_cleared</span>
                    <span className="text-primary font-bold">
                      {result?.stats?.cars_cleared ? "نعم" : "لا"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">admins_remaining</span>
                    <span className="text-[#4ADE80] font-bold">
                      {fmtNum(result?.stats?.admins_remaining ?? 0)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={closeAll}
                  data-testid="reset-reload-btn"
                  className="mt-4 w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-all"
                >
                  إعادة تحميل الصفحة
                </button>
              </>
            ) : (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Warning size={26} weight="fill" className="text-[#EF4444]" />
                    <h3 className="text-lg font-bold text-white">تأكيد نهائي</h3>
                  </div>
                  <button
                    onClick={closeAll}
                    data-testid="reset-cancel-x"
                    disabled={busy}
                    className="text-white/60 hover:text-white transition-colors disabled:opacity-40"
                  >
                    <XIcon size={20} weight="bold" />
                  </button>
                </div>
                <p className="text-white/80 text-sm mb-3">
                  للتأكيد، اكتب في الحقل التالي بالضبط:
                </p>
                <div className="rounded-lg bg-black/40 border border-primary/30 px-3 py-2 mb-3 text-center">
                  <code className="text-primary font-bold text-lg tracking-wider">
                    {CONFIRM_PHRASE}
                  </code>
                </div>
                <input
                  type="text"
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  data-testid="reset-confirm-input"
                  placeholder={CONFIRM_PHRASE}
                  autoComplete="off"
                  spellCheck={false}
                  dir="ltr"
                  className="w-full px-4 py-3 rounded-xl bg-black/40 border-2 border-[#EF4444]/40 focus:border-[#EF4444] text-white font-mono text-center text-lg outline-none transition-colors mb-4"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={closeAll}
                    disabled={busy}
                    data-testid="reset-cancel-btn"
                    className="flex-1 py-3 rounded-xl bg-white/5 border border-white/15 text-white font-semibold hover:bg-white/10 transition-all disabled:opacity-40"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={executeReset}
                    disabled={!canReset}
                    data-testid="reset-execute-btn"
                    className="flex-1 py-3 rounded-xl bg-[#EF4444] text-white font-bold hover:bg-[#DC2626] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busy ? "جارٍ التصفير…" : "تأكيد وتنفيذ"}
                  </button>
                </div>
                <p className="text-[#EF4444]/80 text-xs mt-3 text-center">
                  ⚠️ لا يمكن التراجع عن هذا الإجراء
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
